import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import { Newspaper, Download, Mail, ExternalLink } from 'lucide-react';

export default function Press() {
  const { i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  const stats = [
    { value: '7M+', label: isKorean ? '전 세계 한인' : 'Koreans Worldwide' },
    { value: '8+', label: isKorean ? '지원 국가' : 'Countries Covered' },
    { value: '2026', label: isKorean ? '설립 연도' : 'Year Founded' },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl mx-auto">
      <SEO title="Press & Media" description="Press resources and media enquiries for KoreanHub." path="/press" />
      {/* Hero */}
      <div className="text-center mb-12">
        <div className="w-16 h-16 bg-coral-100 dark:bg-coral-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Newspaper className="w-8 h-8 text-coral-600" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          {isKorean ? '보도자료' : 'Press & Media'}
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {isKorean
            ? 'KoreanHub에 대한 보도자료와 미디어 관련 정보'
            : 'Press releases and media resources for KoreanHub'
          }
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-12">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-white dark:bg-slate-800 rounded-xl p-4 text-center shadow-lg"
          >
            <div className="text-2xl sm:text-3xl font-bold text-coral-500 mb-1">
              {stat.value}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* About */}
      <section className="bg-white dark:bg-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          {isKorean ? 'KoreanHub 소개' : 'About KoreanHub'}
        </h2>
        <div className="space-y-4 text-gray-600 dark:text-gray-300">
          <p>
            {isKorean
              ? 'KoreanHub는 전 세계 700만 한인 디아스포라를 연결하는 커뮤니티 플랫폼입니다. 2026년에 설립된 KoreanHub는 해외 거주 한인들이 새로운 환경에서 성공적으로 정착하고 성장할 수 있도록 돕는 것을 목표로 합니다.'
              : 'KoreanHub is a community platform connecting 7 million Koreans in the global diaspora. Founded in 2026, KoreanHub aims to help Koreans living abroad successfully settle and thrive in their new environments.'
            }
          </p>
          <p>
            {isKorean
              ? '이민, 취업, 주거, 커뮤니티 연결 등 해외 생활에 필요한 모든 정보와 서비스를 한 곳에서 제공하며, 8개국 이상의 한인 커뮤니티를 지원합니다.'
              : 'We provide all the information and services needed for life abroad in one place - from immigration and jobs to housing and community connections - supporting Korean communities in 8+ countries.'
            }
          </p>
        </div>
      </section>

      {/* Press Releases */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          {isKorean ? '보도자료' : 'Press Releases'}
        </h2>

        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg overflow-hidden">
          <div className="p-8 text-center">
            <Newspaper className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {isKorean
                ? '보도자료가 곧 업데이트됩니다'
                : 'Press releases coming soon'
              }
            </p>
          </div>
        </div>
      </section>

      {/* Brand Assets */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          {isKorean ? '브랜드 자료' : 'Brand Assets'}
        </h2>

        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-coral-500 to-coral-600 flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">KoreanHub Logo</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">PNG, SVG formats</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {isKorean
              ? '로고 및 브랜드 자료가 필요하시면 문의 양식을 통해 연락해 주세요.'
              : 'For logo and brand assets, please get in touch through our contact form.'
            }
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-coral-600 hover:text-coral-700 font-medium text-sm"
          >
            <Download className="w-4 h-4" />
            {isKorean ? '브랜드 자료 요청' : 'Request Brand Assets'}
          </Link>
        </div>
      </section>

      {/* Media Contact */}
      <section className="bg-gradient-to-br from-coral-50 to-coral-100 dark:from-coral-900/20 dark:to-coral-800/10 rounded-2xl p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center flex-shrink-0 shadow">
            <Mail className="w-6 h-6 text-coral-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {isKorean ? '미디어 문의' : 'Media Contact'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-3">
              {isKorean
                ? '인터뷰, 기사 작성, 협업 등 미디어 관련 문의는 문의 양식을 이용해 주세요.'
                : 'For interviews, articles, partnerships, or other media inquiries, please use our contact form.'
              }
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-coral-500 text-white rounded-xl font-medium hover:bg-coral-600 transition-colors"
            >
              <Mail className="w-5 h-5" />
              {isKorean ? '미디어 문의하기' : 'Contact our press team'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
